import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Sparkles, Package, Users, Truck, FileText, Banknote, AlertTriangle,
    CheckCircle2, Loader2, ArrowRightLeft, ChevronRight,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import TidyUpDialog from "@/components/TidyUpDialog";
import { toast } from "sonner";

/**
 * Data Health — one dashboard that surfaces every duplicate or orphan that
 * routinely shows up after a Zoho / QuickBooks migration, with a one-click
 * fix for each category.
 */
export default function DataHealth() {
    const { currentOrg } = useAuth();
    const [loading, setLoading] = useState(true);
    const [counts, setCounts] = useState({ items: 0, customers: 0, vendors: 0, bills: 0, bank: 0, missingItemId: 0 });
    const [openTidy, setOpenTidy] = useState(null); // "items" | "customers" | "vendors" | "bills" | null

    const load = async () => {
        setLoading(true);
        try {
            const [items, customers, vendors, bills, banks] = await Promise.all([
                api.get("/items/duplicate-groups").catch(() => ({ data: { groups: [] } })),
                api.get("/customers/duplicate-groups").catch(() => ({ data: { groups: [] } })),
                api.get("/vendors/duplicate-groups").catch(() => ({ data: { groups: [] } })),
                api.get("/bills/duplicate-groups").catch(() => ({ data: { groups: [] } })),
                api.get("/bank-accounts").catch(() => ({ data: { items: [] } })),
            ]);
            // Same-currency duplicate bank names (simple heuristic — two+ accounts
            // sharing the same currency is a candidate for review, not a hard dup).
            const bankAccs = banks.data?.items || [];
            const ccyCount = {};
            for (const a of bankAccs) ccyCount[a.currency] = (ccyCount[a.currency] || 0) + 1;
            const bankDupGroups = Object.values(ccyCount).filter((c) => c > 1).length;

            // Count invoice + bill lines missing item_id as a "data health" signal.
            let missingItemId = 0;
            const [invRes, billRes] = await Promise.all([
                api.get("/invoices").catch(() => ({ data: { items: [] } })),
                api.get("/bills").catch(() => ({ data: { items: [] } })),
            ]);
            for (const d of (invRes.data?.items || [])) {
                for (const ln of d.lines || []) if (!ln.item_id && (ln.description || "").trim()) missingItemId++;
            }
            for (const d of (billRes.data?.items || [])) {
                for (const ln of d.lines || []) if (!ln.item_id && (ln.description || "").trim()) missingItemId++;
            }

            setCounts({
                items: (items.data?.groups || []).length,
                customers: (customers.data?.groups || []).length,
                vendors: (vendors.data?.groups || []).length,
                bills: (bills.data?.groups || []).length,
                bank: bankDupGroups,
                missingItemId,
            });
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (currentOrg) load(); }, [currentOrg]);

    const totalIssues = counts.items + counts.customers + counts.vendors + counts.bills + counts.bank;

    const section = (kind, title, icon, desc, groups, cta) => {
        const Icon = icon;
        const clean = groups === 0;
        return (
            <div className="rounded-3xl bg-white border border-sand-200 shadow-card p-5 sm:p-6 flex items-start gap-4" data-testid={`health-section-${kind}`}>
                <div className={`h-11 w-11 rounded-2xl grid place-items-center shrink-0 ${clean ? "bg-emerald-50 text-emerald-600" : "bg-secondary/10 text-secondary"}`}>
                    <Icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                        <h3 className="font-heading text-lg text-ocean-700">{title}</h3>
                        {clean ? (
                            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] uppercase tracking-wider">
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Clean
                            </Badge>
                        ) : (
                            <Badge className="bg-coral/15 text-coral-dark text-[10px] uppercase tracking-wider">
                                {groups} {groups === 1 ? "group" : "groups"}
                            </Badge>
                        )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{desc}</p>
                </div>
                {cta}
            </div>
        );
    };

    return (
        <div>
            <PageHeader
                icon={Sparkles}
                title="Data health"
                subtitle="Find and clean duplicate or orphan records in one place — perfect after a Zoho or QuickBooks migration."
            />
            {loading ? (
                <div className="py-20 flex items-center justify-center text-sm text-muted-foreground">
                    <Loader2 className="h-5 w-5 mr-2 animate-spin text-secondary" /> Scanning your workspace…
                </div>
            ) : (
                <>
                    <div className={`rounded-3xl p-5 mb-6 ${totalIssues ? "bg-secondary/5 border border-secondary/30" : "bg-emerald-50 border border-emerald-200"}`} data-testid="health-summary">
                        <div className="flex items-start gap-3">
                            {totalIssues ? (
                                <AlertTriangle className="h-6 w-6 text-secondary-dark mt-0.5 shrink-0" />
                            ) : (
                                <CheckCircle2 className="h-6 w-6 text-emerald-600 mt-0.5 shrink-0" />
                            )}
                            <div>
                                <div className="font-heading text-lg text-ocean-700">
                                    {totalIssues
                                        ? `Found ${totalIssues} group${totalIssues === 1 ? "" : "s"} of likely duplicates.`
                                        : "Your workspace looks clean."}
                                </div>
                                <p className="text-sm text-muted-foreground mt-1">
                                    {totalIssues
                                        ? "Review each section below and tidy the ones you want to fix. Merges re-point every invoice, bill, and payment at the canonical record — no financial data is lost."
                                        : "Nothing to clean up right now. Come back here after your next CSV import or any large data change."}
                                </p>
                                {counts.missingItemId > 0 && (
                                    <div className="mt-3 text-xs text-muted-foreground inline-flex items-center gap-1.5 bg-sand-100 rounded-full px-2.5 py-1">
                                        <span className="h-1.5 w-1.5 rounded-full bg-secondary" />
                                        {counts.missingItemId} line{counts.missingItemId === 1 ? "" : "s"} across invoices/bills has no item_id linked — usually from CSV imports before your catalog was seeded.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {section("items", "Duplicate items", Package, "Merge items whose names differ only in case, punctuation, or plural form. Invoice and bill lines will re-point to the canonical.", counts.items,
                            counts.items > 0
                                ? <Button onClick={() => setOpenTidy("items")} className="bg-ocean-700 text-white rounded-xl" data-testid="health-tidy-items"><ArrowRightLeft className="h-4 w-4 mr-1.5" /> Tidy items</Button>
                                : <Link to="/app/items" className="text-xs text-muted-foreground hover:text-ocean-700">Manage →</Link>
                        )}
                        {section("customers", "Duplicate customers", Users, "Merge customers with near-identical names (ignoring Ltd / Inc / LLC) or matching email. Invoices and customer payments move to the kept record.", counts.customers,
                            counts.customers > 0
                                ? <Button onClick={() => setOpenTidy("customers")} className="bg-ocean-700 text-white rounded-xl" data-testid="health-tidy-customers"><ArrowRightLeft className="h-4 w-4 mr-1.5" /> Tidy customers</Button>
                                : <Link to="/app/customers" className="text-xs text-muted-foreground hover:text-ocean-700">Manage →</Link>
                        )}
                        {section("vendors", "Duplicate vendors", Truck, "Merge near-identical vendors. Bills, bill-payments, and linked expenses move to the canonical vendor.", counts.vendors,
                            counts.vendors > 0
                                ? <Button onClick={() => setOpenTidy("vendors")} className="bg-ocean-700 text-white rounded-xl" data-testid="health-tidy-vendors"><ArrowRightLeft className="h-4 w-4 mr-1.5" /> Tidy vendors</Button>
                                : <Link to="/app/vendors" className="text-xs text-muted-foreground hover:text-ocean-700">Manage →</Link>
                        )}
                        {section("bills", "Duplicate bills", FileText, "Bills with the same vendor, issue date, and total. Pick the canonical and delete the rest — linked payments cascade cleanly.", counts.bills,
                            counts.bills > 0
                                ? <Button onClick={() => setOpenTidy("bills")} className="bg-coral hover:bg-coral-dark text-white rounded-xl" data-testid="health-tidy-bills"><ChevronRight className="h-4 w-4 mr-1.5" /> Clean bills</Button>
                                : <Link to="/app/bills" className="text-xs text-muted-foreground hover:text-ocean-700">Manage →</Link>
                        )}
                        {section("bank", "Same-currency bank accounts", Banknote, "More than one bank account shares the same currency. Review on the Banking page — use Merge if they are the same account in different systems.", counts.bank,
                            <Link to="/app/banking" className="text-xs font-medium text-secondary-dark hover:text-secondary hover:underline" data-testid="health-tidy-bank">Open banking →</Link>
                        )}
                    </div>

                    {/* Tidy dialogs — only rendered when opened so they refetch each time */}
                    {openTidy === "items" && (
                        <TidyUpDialog open onOpenChange={(v) => !v && setOpenTidy(null)}
                            label="item" fetchUrl="/items/duplicate-groups" mergeUrl="/items/merge" onDone={load}
                            renderRow={(m, isC) => (
                                <div>
                                    <div className={`font-medium text-sm ${isC ? "text-secondary-dark" : "text-ocean-700"}`}>{m.name}</div>
                                    {m.description && <div className="text-[11px] text-muted-foreground truncate">{m.description}</div>}
                                    <div className="text-[11px] text-muted-foreground">{m.item_type === "both" ? "Sales / Purchase" : m.item_type}</div>
                                </div>
                            )}
                        />
                    )}
                    {openTidy === "customers" && (
                        <TidyUpDialog open onOpenChange={(v) => !v && setOpenTidy(null)}
                            label="customer" fetchUrl="/customers/duplicate-groups" mergeUrl="/customers/merge" onDone={load}
                            renderRow={(m, isC) => (
                                <div>
                                    <div className={`font-medium text-sm ${isC ? "text-secondary-dark" : "text-ocean-700"}`}>{m.name}</div>
                                    <div className="text-[11px] text-muted-foreground truncate">
                                        {m.email || ""}{m.email && m.phone && " · "}{m.phone || ""}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">
                                        {(m.usage?.invoices || 0)} invoice{(m.usage?.invoices) === 1 ? "" : "s"}
                                    </div>
                                </div>
                            )}
                        />
                    )}
                    {openTidy === "vendors" && (
                        <TidyUpDialog open onOpenChange={(v) => !v && setOpenTidy(null)}
                            label="vendor" fetchUrl="/vendors/duplicate-groups" mergeUrl="/vendors/merge" onDone={load}
                            renderRow={(m, isC) => (
                                <div>
                                    <div className={`font-medium text-sm ${isC ? "text-secondary-dark" : "text-ocean-700"}`}>{m.name}</div>
                                    <div className="text-[11px] text-muted-foreground truncate">
                                        {m.email || ""}{m.email && m.phone && " · "}{m.phone || ""}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">
                                        {(m.usage?.bills || 0)} bill{(m.usage?.bills) === 1 ? "" : "s"}
                                    </div>
                                </div>
                            )}
                        />
                    )}
                    {openTidy === "bills" && (
                        <TidyUpDialog open onOpenChange={(v) => !v && setOpenTidy(null)}
                            label="bill" title="Clean duplicate bills"
                            fetchUrl="/bills/duplicate-groups" mergeUrl={null} deleteUrlPrefix="/bills/"
                            onDone={load}
                            renderRow={(m, isC) => (
                                <div>
                                    <div className={`font-medium text-sm ${isC ? "text-secondary-dark" : "text-ocean-700"}`}>
                                        {m.number} · {m.vendor_name}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">
                                        {m.issue_date} · {Number(m.total).toFixed(2)} {m.currency} · {m.status}
                                        {Number(m.amount_paid || 0) > 0 && <span className="ml-1 text-secondary-dark">· {Number(m.amount_paid).toFixed(2)} paid</span>}
                                    </div>
                                </div>
                            )}
                        />
                    )}
                </>
            )}
        </div>
    );
}
